import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, Card, CardContent } from '@mui/material';
import { Add } from '@mui/icons-material';
import JerseyEditor from '../components/JerseyEditor/JerseyEditor';

const JerseyManagement: React.FC = () => {
  const [selectedJersey, setSelectedJersey] = useState<any>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const navigate = useNavigate();

  // Check for prefill from URL
  const searchParams = new URLSearchParams(window.location.search);
  const prefill = searchParams.get('prefill') || searchParams.get('colours') || '';

  const handleCreateNew = () => {
    setSelectedJersey(null);
    setIsCreating(true);
    setIsEditorOpen(true);
  };

  const handleEditorClose = () => {
    setIsEditorOpen(false);
    setSelectedJersey(null);
    setIsCreating(false);
    // If we were opened via url with action=create, clean it up
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'create' || params.get('prefill') || params.get('colours')) {
      navigate('/jerseys');
    }
  };

  // Auto-open editor if arriving with ?action=create and prefill present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const hasPrefill = !!(params.get('prefill') || params.get('colours'));
    if (action === 'create' && hasPrefill && !isEditorOpen) {
      setIsCreating(true);
      setSelectedJersey(null);
      setIsEditorOpen(true);
    }
  }, [isEditorOpen]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Jersey Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateNew}
        >
          Create New Jersey
        </Button>
      </Box>

      {/* Helper Card */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <Card sx={{ maxWidth: 720, width: '100%', boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Get started creating a Jersey
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Use the button above to open the editor and enter a colours string. We support plain text like:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mt: 0 }}>
              <li>
                <Typography variant="body2">Grey, Light Blue Crossed Sashes, Red Armbands and Cap</Typography>
              </li>
              <li>
                <Typography variant="body2">Royal Blue, Yellow Crown (b and f), Sleeves and Cap With Royal Blue Seams</Typography>
              </li>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              You can also prefill from a URL by appending <code>?action=create&amp;prefill=&lt;your text&gt;</code> to the Jerseys page.
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={handleCreateNew}>
              Create New Jersey
            </Button>
          </CardContent>
        </Card>
      </Box>

      {/* Jersey Editor Dialog */}
      <Dialog
        open={isEditorOpen}
        onClose={handleEditorClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {isCreating ? 'Create New Jersey' : 'Edit Jersey'}
        </DialogTitle>
        <DialogContent>
          <JerseyEditor
            key={isCreating ? `create::${prefill}` : `edit::${selectedJersey?.id ?? 'new'}`}
            jerseyId={selectedJersey?.id}
            onSave={handleEditorClose}
            onCancel={handleEditorClose}
            initialDescription={isCreating ? prefill : selectedJersey?.description}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default JerseyManagement;
